import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './rec-tuning-panel.module.scss';

export type HashtagGroup = {
  group: string;
  tags: ReadonlyArray<string>;
};

export type SelectedHashtag = {
  tag: string;
  provenance: 'brief' | 'manual';
  /** 0–100 slider position shown in the UI. */
  weight: number;
};

const styles = {
  root: pickCss(css, css.root),
  head: pickCss(css, css.head),
  headTitle: pickCss(css, css.headTitle),
  headSub: pickCss(css, css.headSub),
  divider: pickCss(css, css.divider),
  section: pickCss(css, css.section),
  sectionHead: pickCss(css, css.sectionHead),
  sectionTitle: pickCss(css, css.sectionTitle),
  sectionHint: pickCss(css, css.sectionHint),
  countBadge: pickCss(css, css.countBadge),
  chevron: pickCss(css, css.chevron),
  weightRows: pickCss(css, css.weightRows),
  weightRow: pickCss(css, css.weightRow),
  weightTagCol: pickCss(css, css.weightTagCol),
  weightTag: pickCss(css, css.weightTag),
  weightPct: pickCss(css, css.weightPct),
  weightInput: pickCss(css, css.weightInput),
  weightRemove: pickCss(css, css.weightRemove),
  legend: pickCss(css, css.legend),
  legendDot: pickCss(css, css.legendDot),
  search: pickCss(css, css.search),
  searchInput: pickCss(css, css.searchInput),
  libCard: pickCss(css, css.libCard),
  libGroup: pickCss(css, css.libGroup),
  libGroupName: pickCss(css, css.libGroupName),
  libTags: pickCss(css, css.libTags),
  libTag: pickCss(css, css.libTag),
  empty: pickCss(css, css.empty),
};

type RecTuningPanelProps = {
  selectedHashtags: ReadonlyArray<SelectedHashtag>;
  hashtagLibrary: ReadonlyArray<HashtagGroup>;
  onAddHashtag: (tag: string) => void;
  onRemoveHashtag: (tag: string) => void;
  onHashtagWeightChange: (tag: string, value: number) => void;
};

/**
 * RecTuningPanel — pen frames `TeHTQ` / `FEOIE` / `eY8iZ`. Per-hashtag weight
 * sliders plus a searchable hashtag library. Each section header collapses.
 */
export const RecTuningPanel = ({
  selectedHashtags,
  hashtagLibrary,
  onAddHashtag,
  onRemoveHashtag,
  onHashtagWeightChange,
}: RecTuningPanelProps) => {
  const [selectedOpen, setSelectedOpen] = useState(true);
  const [libraryOpen, setLibraryOpen] = useState(true);
  const [search, setSearch] = useState('');

  const selectedSet = useMemo(
    () => new Set(selectedHashtags.map((h) => h.tag)),
    [selectedHashtags]
  );

  const hasBriefTags = selectedHashtags.some((h) => h.provenance === 'brief');
  const hasManualTags = selectedHashtags.some((h) => h.provenance === 'manual');

  const totalTagCount = useMemo(
    () => hashtagLibrary.reduce((sum, g) => sum + g.tags.length, 0),
    [hashtagLibrary]
  );

  const filteredLibrary = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hashtagLibrary
      .map((group) => ({
        group: group.group,
        tags: group.tags.filter(
          (tag) => q === '' || tag.toLowerCase().includes(q)
        ),
      }))
      .filter((group) => group.tags.length > 0);
  }, [hashtagLibrary, search]);

  const selectedSummary = useMemo(() => {
    if (selectedHashtags.length === 0) return 'No hashtags selected';
    const head = selectedHashtags
      .slice(0, 2)
      .map((h) => `${h.tag} ${Math.round(h.weight)}%`)
      .join(' · ');
    const more = selectedHashtags.length - 2;
    return more > 0 ? `${head} +${more} more` : head;
  }, [selectedHashtags]);

  const librarySummary =
    totalTagCount > 0
      ? `${selectedHashtags.length} of ${totalTagCount} active · tap to expand`
      : 'tap to expand';

  const libraryHint =
    totalTagCount > 0
      ? `All ${totalTagCount} tags · tap to add or remove`
      : 'tap to add or remove';

  return (
    <section className={styles.root()}>
      <div className={styles.head()}>
        <span className={styles.headTitle()}>Recommendation tuning</span>
        <span className={styles.headSub()}>
          Global for all days · drag hashtag weights to steer picks
        </span>
      </div>

      <div className={styles.section()}>
        <button
          aria-expanded={selectedOpen}
          className={styles.sectionHead()}
          onClick={() => setSelectedOpen((o) => !o)}
          type="button"
        >
          <span className={styles.sectionTitle()}>Selected hashtags</span>
          <span className={styles.countBadge()}>{selectedHashtags.length}</span>
          <span className={styles.sectionHint()}>
            {selectedOpen
              ? 'drag bars — higher weight boosts search results'
              : selectedSummary}
          </span>
          <span aria-hidden="true" className={styles.chevron()}>
            {selectedOpen ? (
              <ChevronDown size={13} />
            ) : (
              <ChevronRight size={13} />
            )}
          </span>
        </button>
        {selectedOpen && (
          <>
            {selectedHashtags.length > 0 ? (
              <>
                <div className={styles.weightRows()}>
                  {selectedHashtags.map((h) => {
                    const isBrief = h.provenance === 'brief';
                    return (
                      <div
                        className={joinCss(
                          styles.weightRow({ isBrief, isManual: !isBrief })
                        )}
                        key={h.tag}
                      >
                        <div className={styles.weightTagCol()}>
                          <span
                            className={styles.weightTag({
                              isBrief,
                              isManual: !isBrief,
                            })}
                          >
                            #{h.tag}
                          </span>
                          <span className={styles.weightPct()}>
                            {Math.round(h.weight)}%
                          </span>
                        </div>
                        <input
                          aria-label={`${h.tag} weight`}
                          className={styles.weightInput({
                            isBrief,
                            isManual: !isBrief,
                          })}
                          max={100}
                          min={0}
                          onChange={(e) =>
                            onHashtagWeightChange(h.tag, Number(e.target.value))
                          }
                          step={5}
                          type="range"
                          value={h.weight}
                        />
                        <button
                          aria-label={`Remove ${h.tag}`}
                          className={styles.weightRemove({
                            isBrief,
                            isManual: !isBrief,
                          })}
                          onClick={() => onRemoveHashtag(h.tag)}
                          type="button"
                        >
                          <X aria-hidden="true" size={10} />
                        </button>
                      </div>
                    );
                  })}
                </div>
                {(hasBriefTags || hasManualTags) && (
                  <span className={styles.legend()}>
                    {hasBriefTags && (
                      <>
                        <span
                          aria-hidden="true"
                          className={styles.legendDot({ isBrief: true })}
                        />
                        from brief · green bar
                      </>
                    )}
                    {hasManualTags && (
                      <>
                        <span
                          aria-hidden="true"
                          className={styles.legendDot({ isManual: true })}
                        />
                        added manually · purple bar
                      </>
                    )}
                  </span>
                )}
              </>
            ) : (
              <p className={styles.empty()}>
                No hashtags selected — add from the library below.
              </p>
            )}
          </>
        )}
      </div>

      <span aria-hidden="true" className={styles.divider()} />

      <div className={styles.section()}>
        <button
          aria-expanded={libraryOpen}
          className={styles.sectionHead()}
          onClick={() => setLibraryOpen((o) => !o)}
          type="button"
        >
          <span className={styles.sectionTitle()}>Hashtag library</span>
          <span className={styles.sectionHint()}>
            {libraryOpen ? libraryHint : librarySummary}
          </span>
          <span aria-hidden="true" className={styles.chevron()}>
            {libraryOpen ? (
              <ChevronDown size={13} />
            ) : (
              <ChevronRight size={13} />
            )}
          </span>
        </button>
        {libraryOpen && (
          <>
            <div className={styles.search()}>
              <Search aria-hidden="true" size={11} />
              <input
                className={styles.searchInput()}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search hashtags"
                type="search"
                value={search}
              />
            </div>
            <div className={styles.libCard()}>
              {filteredLibrary.map((group) => (
                <div
                  className={styles.libGroup()}
                  key={group.group || 'all-tags'}
                >
                  {group.group && (
                    <span className={styles.libGroupName()}>{group.group}</span>
                  )}
                  <div className={styles.libTags()}>
                    {group.tags.map((tag) => {
                      const isSelected = selectedSet.has(tag);
                      return (
                        <button
                          aria-pressed={isSelected}
                          className={styles.libTag({ isSelected })}
                          key={tag}
                          onClick={() =>
                            isSelected
                              ? onRemoveHashtag(tag)
                              : onAddHashtag(tag)
                          }
                          type="button"
                        >
                          #{tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {filteredLibrary.length === 0 && (
                <p className={styles.empty()}>No hashtags match your search.</p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
